// Tutoriales in-app (#1013) — se monta una vez junto a las rutas (App.tsx).
// Carga las reglas del usuario y pinta el overlay y el botón de ayuda. No abre
// nada por su cuenta: los tutoriales se arrancan desde el "?" o desde Ayuda.
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useUserStore } from "../store/userStore";
import { useDarkMode } from "../context/ThemeContext";
import { useHelpFlagOn } from "../Layouts/help/helpFlag";
import { canSee, pathMatches, tutorialsForPath } from "./eligibility";
import { useTutorialsStore } from "./store";
import TutorialOverlay from "./TutorialOverlay";
import { currentUserId, langOf } from "./utils";
import { SUPPORT_TYPES, SUPPORT_TYPE_META, type SupportType } from "../Layouts/help/support/supportRequest";

/** Pantallas sin sesión o de paso: ahí nunca se ofrece nada. */
const NO_TUTORIAL_ROUTES = ["/login", "/register", "/splash", "/soporte", "/purchase", "/tracking", "/tienda"];
// El formulario de soporte (#1088) sólo se descarga cuando se abre.
const SupportRequestDialog = lazy(() => import("../Layouts/help/support/SupportRequestDialog"));

export default function TutorialHost() {
  const user = useUserStore((s) => s.user);
  const uid = currentUserId(user);
  const location = useLocation();

  const load = useTutorialsStore((s) => s.load);
  const reset = useTutorialsStore((s) => s.reset);
  const rules = useTutorialsStore((s) => s.rules);
  const ensureCatalog = useTutorialsStore((s) => s.ensureCatalog);

  // El catálogo (toda la wiki) sólo se descarga si al usuario le tocan tutoriales:
  // el botón "?" lo necesita para listar los de la pantalla actual.
  useEffect(() => {
    if (rules?.active) void ensureCatalog();
  }, [rules?.active, rules?.platformAdmin, ensureCatalog]);

  // Reglas por sesión: al cerrar sesión se olvidan, al entrar otro usuario se recargan.
  useEffect(() => {
    if (!uid) {
      reset();
      return;
    }
    const controller = new AbortController();
    void load(uid, controller.signal);
    return () => controller.abort();
  }, [uid, load, reset]);

  const path = location.pathname;
  const blockedRoute = path === "/" || NO_TUTORIAL_ROUTES.some((r) => pathMatches(path, r));

  /*
    Los tutoriales NO se abren solos.

    Antes, al entrar a una pantalla, el overlay aparecía a los 900 ms y tapaba
    lo que el usuario iba a hacer. Aunque fuera «como mucho uno por pantalla»,
    quien recorre seis secciones seguidas recibe seis interrupciones sobre una
    herramienta que ya sabe usar, y cada una hay que cerrarla antes de seguir.

    Siguen estando enteros, a un clic: el botón "?" de abajo a la derecha lista
    los de la pantalla actual, y la app Ayuda los lista todos. Lo que se quita
    es que el programa decida por el usuario cuándo mirarlos.

    Queda deliberadamente sin usar la maquinaria de `shouldAutoStart`
    —«no volver a mostrar», tutoriales vistos, descartados— por si algún día se
    quiere ofrecer el primero al estrenar cuenta: volver a encenderlo es este
    efecto y nada más.
  */

  // Si se navega a otra pantalla con un tutorial abierto, se cierra sin marcarlo.
  const lastPath = useRef(path);
  useEffect(() => {
    if (lastPath.current === path) return;
    lastPath.current = path;
    const current = useTutorialsStore.getState().active;
    if (current && !current.tutorial.routes.some((r) => pathMatches(path, r)) && !current.manual) {
      useTutorialsStore.getState().close();
    }
  }, [path]);

  if (!uid || blockedRoute) return null;

  return (
    <>
      <TutorialOverlay />
      {/* Queda debajo del overlay: el tutorial de bienvenida lo resalta.
          Siempre visible con sesión (#1088): soporte no depende de que los
          tutoriales estén activos ni de la bandera de «Ayuda y Tutoriales». */}
      <HelpLauncher path={path} />
    </>
  );
}

/**
 * Otro elemento flotante que ocupa la esquina inferior derecha (p. ej. el
 * softphone del CRM durante una llamada) se marca con
 * `data-floating-corner="bottom-right"`; mientras exista, el "?" se oculta
 * para no taparlo ni quedar debajo.
 */
export const FLOATING_CORNER_SELECTOR = '[data-floating-corner="bottom-right"]';

function useCornerTaken(): boolean {
  const [taken, setTaken] = useState(false);
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
    const check = () => setTaken(document.querySelector(FLOATING_CORNER_SELECTOR) !== null);
    check();
    const observer = new MutationObserver(check);
    // Sólo altas/bajas de nodos y cambios del propio atributo marcador: el resto
    // de atributos (clases, estilos…) cambian sin parar y no importan aquí.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-floating-corner"],
    });
    return () => observer.disconnect();
  }, []);
  return taken;
}

/**
 * Botón flotante "?": app Ayuda con los tutoriales de esta pantalla como
 * subnodos (sólo si los tutoriales están activos para el usuario) + contactar a
 * soporte (#1088). El grupo de Ayuda desaparece entero si la app «Ayuda y
 * Tutoriales» está apagada en Feature Flags; soporte se ve siempre.
 */
export function HelpLauncher({ path }: { path: string }) {
  const { i18n } = useTranslation();
  const lang = langOf(i18n.language);
  const tr = (es: string, en: string) => (lang === "en" ? en : es);
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const rules = useTutorialsStore((s) => s.rules);
  const start = useTutorialsStore((s) => s.start);
  const [open, setOpen] = useState(false);
  const [support, setSupport] = useState<SupportType | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => setOpen(false), [path]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const catalog = useTutorialsStore((s) => s.catalog);
  const helpOn = useHelpFlagOn();
  const tutorialsOn = helpOn && !!rules?.active;
  const here = tutorialsOn && catalog ? tutorialsForPath(path, catalog).filter((t) => canSee(t, rules)) : [];
  const cornerTaken = useCornerTaken();
  // En el mapa, la esquina inferior derecha la ocupan sus controles.
  const lifted = pathMatches(path, "/map");

  const menu = isDarkMode ? "bg-dark-card border-dark-border text-white" : "bg-white border-gray-200 text-gray-900";
  const item = isDarkMode ? "hover:bg-dark-hover" : "hover:bg-gray-100";

  // El formulario de soporte, si está abierto, se conserva aunque se oculte el
  // botón: va SIEMPRE en la misma posición del árbol (fuera del div del botón)
  // para que React no lo desmonte al aparecer el softphone — se perdería lo
  // escrito y podría duplicarse el ticket. Se pinta con un portal en <body>.
  const dialog =
    support !== null ? (
      <Suspense fallback={null}>
        <SupportRequestDialog open type={support} onClose={() => setSupport(null)} />
      </Suspense>
    ) : null;

  return (
    <>
      {dialog}
      {!cornerTaken && (
        <div ref={ref} className={`fixed z-30 ${lifted ? "right-3 top-[8.5rem] sm:top-[8.25rem]" : "right-4 bottom-4"}`}>
          {open && (
            <div role="menu" className={`absolute right-0 ${lifted ? "top-11" : "bottom-14"} w-72 max-w-[calc(100vw-1.5rem)] max-h-[60vh] overflow-y-auto rounded-xl border shadow-xl ${menu}`}>
              {helpOn && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    navigate("/ayuda");
                  }}
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left text-sm ${item}`}
                >
                  <i className="ri-book-open-line mt-0.5 text-lg text-primary-500" aria-hidden="true" />
                  <span>
                    <span className="block font-semibold">{tr("Ayuda y Tutoriales", "Help & Tutorials")}</span>
                    <span className="block text-xs opacity-70">{tr("Guías paso a paso de cada app", "Step-by-step guides for every app")}</span>
                  </span>
                </button>
              )}
              {/* Tutoriales de esta pantalla: subnodos de Ayuda, como los tipos de soporte. */}
              {here.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="menuitem"
                  title={tr("Tutorial de esta pantalla", "Tutorial for this screen")}
                  onClick={() => {
                    setOpen(false);
                    start(t.id, { manual: true });
                  }}
                  className={`flex w-full items-center gap-3 px-4 py-2 pl-11 text-left text-sm ${item}`}
                >
                  <i className="ri-play-circle-line text-base text-primary-500" aria-hidden="true" />
                  {tr("Tutorial", "Tutorial")}: {t.title[lang]}
                </button>
              ))}
              <div className={`px-4 pb-2 pt-3 ${helpOn ? `border-t ${isDarkMode ? "border-dark-border" : "border-gray-200"}` : ""}`}>
                <p className="flex items-center gap-2 text-sm font-semibold">
                  <i className="ri-customer-service-2-line text-lg text-primary-500" aria-hidden="true" />
                  {tr("Contactar a soporte", "Contact support")}
                </p>
                <p className="mb-1 text-xs opacity-70">
                  {tr("Deja tu duda, sugerencia o reporta un bug", "Send a question or suggestion, or report a bug")}
                </p>
              </div>
              {SUPPORT_TYPES.map((k) => (
                <button
                  key={k}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    setSupport(k);
                  }}
                  className={`flex w-full items-center gap-3 px-4 py-2 pl-11 text-left text-sm ${item}`}
                >
                  <i className={`${SUPPORT_TYPE_META[k].icon} text-base text-primary-500`} aria-hidden="true" />
                  {SUPPORT_TYPE_META[k].label[lang]}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            data-tour="help-launcher"
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={tr("Ayuda", "Help")}
            title={tr("Ayuda", "Help")}
            className={`flex items-center justify-center rounded-full bg-primary-500 ${lifted ? "h-9 w-9" : "h-11 w-11"} text-white shadow-lg transition hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300`}
          >
            <i className="ri-graduation-cap-line text-xl" aria-hidden="true" />
          </button>
        </div>
      )}
    </>
  );
}
